// =============================================================================
// GiftCardsScreen — physical gift cards (cadeaubon): find a card by code or
// serial, and (booking_gift_card_manage) create print batches and export
// their codes for the printer.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { formatError } from '../lib/errors';
import { formatVenueDate, formatVenueDateTime } from '../lib/time';
import { downloadCsv } from '../lib/csv';
import {
  createGiftCardBatch,
  exportGiftCardBatch,
  fetchGiftCardLiability,
  formatEuro,
  giftCardBatchCsvRows,
  giftCardStatusOf,
  listGiftCardBatches,
  parseGiftCardScan,
  searchGiftCards,
  type GiftCardBatch,
  type GiftCardLiability,
  type GiftCardSearchRow,
} from '../lib/giftcard';

export function GiftCardsScreen() {
  const { can } = useAuth();
  const canManage = can('booking_gift_card_manage');
  const canSeeTotals = canManage || can('booking_view_analytics');

  return (
    <div className="screen vouchers-screen giftcards-screen">
      <div className="vouchers-toolbar">
        <h1>Cadeaubonnen</h1>
      </div>
      <p className="muted">
        Fysieke cadeaukaarten met QR-code. Een kaart is niet geactiveerd tot hij aan de
        balie op de pinautomaat wordt opgewaardeerd: scan hem via <Link to="/scan">Scan</Link>.
      </p>
      {canSeeTotals ? <GiftCardTotals /> : null}
      <GiftCardSearch />
      {canManage ? <GiftCardBatches /> : null}
    </div>
  );
}

// What is still on the cards: a liability until it is spent (or expires).
function GiftCardTotals() {
  const [t, setT] = useState<GiftCardLiability | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetchGiftCardLiability().then(setT).catch((e) => setError(formatError(e)));
  }, []);
  if (error) return <div className="error-card">{error}</div>;
  if (!t) return null;
  return (
    <section className="giftcards-section">
      <h2>Saldo op kaarten</h2>
      <div className="detail-grid">
        <section>
          <dl>
            <dt>Openstaand</dt><dd><strong>{formatEuro(t.outstanding_cents)}</strong> <span className="muted">op {t.cards_active} actieve kaarten</span></dd>
            {t.held_cents > 0 ? (<><dt>Gereserveerd</dt><dd>{formatEuro(t.held_cents)} <span className="muted">(lopende betalingen)</span></dd></>) : null}
            <dt>Verlopen</dt><dd>{formatEuro(t.expired_cents)}</dd>
            {t.blocked_cents > 0 ? (<><dt>Geblokkeerd</dt><dd>{formatEuro(t.blocked_cents)}</dd></>) : null}
          </dl>
        </section>
        <section>
          <dl>
            <dt>Opgewaardeerd</dt><dd>{formatEuro(t.loaded_cents)}</dd>
            <dt>Besteed</dt><dd>{formatEuro(t.spent_cents)}</dd>
            <dt>Teruggezet</dt><dd>{formatEuro(t.credited_back_cents)}</dd>
            <dt>Kaarten</dt><dd>{t.cards_total} <span className="muted">({t.cards_inactive} niet geactiveerd)</span></dd>
          </dl>
        </section>
      </div>
    </section>
  );
}

function GiftCardSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<GiftCardSearchRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    // A full code (or a pasted QR link) goes straight to the card.
    const exact = parseGiftCardScan(q);
    if (exact) {
      navigate(`/cadeaubon/${exact}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setRows(await searchGiftCards(q));
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }, [query, navigate]);

  return (
    <section className="giftcards-section">
      <h2>Zoeken</h2>
      <form
        className="giftcards-search"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <input
          type="search"
          className="vouchers-filter"
          placeholder="Code (min. 4 tekens) of serienummer"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoCapitalize="characters"
        />
        <button type="submit" className="btn-secondary" disabled={busy || !query.trim()}>
          Zoek
        </button>
      </form>
      {error ? <div className="error-card">{error}</div> : null}
      {rows && rows.length === 0 ? <div className="muted">Geen kaarten gevonden.</div> : null}
      {rows && rows.length > 0 ? (
        <table className="vouchers-table">
          <thead>
            <tr>
              <th>Serie</th>
              <th>Code</th>
              <th>Saldo</th>
              <th>Geldig t/m</th>
              <th>Status</th>
              <th>Batch</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const st = giftCardStatusOf(r.display_status);
              return (
                <tr key={r.id}>
                  <td>{r.serial}</td>
                  <td><Link to={`/cadeaubon/${r.code}`} className="voucher-code-link">{r.code_display}</Link></td>
                  <td>{formatEuro(r.balance_cents)}</td>
                  <td>{r.expires_at ? formatVenueDate(r.expires_at) : '—'}</td>
                  <td><span className={`voucher-pill ${st.className}`}>{st.label}</span></td>
                  <td className="muted">{r.batch_label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

function GiftCardBatches() {
  const [batches, setBatches] = useState<GiftCardBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setBatches(await listGiftCardBatches());
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleExport = useCallback(async (b: GiftCardBatch) => {
    const ok = window.confirm(
      `Codes van "${b.label}" (${b.card_count} kaarten) downloaden voor de drukker?\n\n` +
        'Dit bestand is gevoelig: wie het heeft, kan een kaart besteden zodra die is ' +
        'opgewaardeerd. Stuur het alleen naar de drukker en verwijder het daarna. ' +
        'Elke download wordt vastgelegd.',
    );
    if (!ok) return;
    setExporting(b.id);
    setError(null);
    try {
      const exp = await exportGiftCardBatch(b.id);
      const safe = b.label.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '') || 'batch';
      downloadCsv(`cadeaubonnen-${safe}.csv`, giftCardBatchCsvRows(exp));
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setExporting(null);
    }
  }, [load]);

  return (
    <section className="giftcards-section">
      <div className="giftcards-section-head">
        <h2>Drukbatches</h2>
        <button className="walkin-primary-btn" onClick={() => setShowCreate(true)}>
          + Nieuwe batch
        </button>
      </div>
      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}
      {!loading && batches.length === 0 ? <div className="muted">Nog geen batches.</div> : null}
      {batches.length > 0 ? (
        <table className="vouchers-table">
          <thead>
            <tr>
              <th>Batch</th>
              <th>Kaarten</th>
              <th>Serienummers</th>
              <th>Actief</th>
              <th>Saldo</th>
              <th>Aangemaakt</th>
              <th>Gedownload</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id}>
                <td>
                  {b.label}
                  {b.notes ? <div className="muted">{b.notes}</div> : null}
                </td>
                <td>{b.card_count}</td>
                <td>{b.serial_from}–{b.serial_to}</td>
                <td>
                  {b.active_count}
                  {b.blocked_count > 0 ? <span className="muted"> · {b.blocked_count} geblokkeerd</span> : null}
                </td>
                <td>{formatEuro(b.balance_cents)}</td>
                <td>
                  {formatVenueDate(b.created_at)}
                  {b.created_by_email ? <div className="muted">{b.created_by_email}</div> : null}
                </td>
                <td>
                  {b.export_count > 0 && b.exported_at
                    ? `${b.export_count}× · ${formatVenueDateTime(b.exported_at)}`
                    : '—'}
                </td>
                <td>
                  <button
                    className="btn-link"
                    disabled={exporting === b.id}
                    onClick={() => void handleExport(b)}
                  >
                    {exporting === b.id ? 'Bezig…' : 'CSV voor drukker'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {showCreate ? (
        <CreateBatchModal
          onClose={(created) => {
            setShowCreate(false);
            if (created) void load();
          }}
        />
      ) : null}
    </section>
  );
}

function CreateBatchModal({ onClose }: { onClose: (created: boolean) => void }) {
  const [count, setCount] = useState('100');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = Number(count);
  const valid = Number.isInteger(n) && n >= 1 && n <= 5000 && label.trim().length > 0;

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await createGiftCardBatch(n, label.trim(), notes.trim() || null);
      onClose(true);
    } catch (e) {
      setError(formatError(e));
      setBusy(false);
    }
  }, [n, label, notes, onClose]);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ / Esc only */ }}>
      <div className="dep-modal voucher-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div><h2>Nieuwe drukbatch</h2></div>
          <button className="btn-link dep-modal-close" onClick={() => onClose(false)} aria-label="Sluiten">✕</button>
        </header>
        <div className="dep-modal-body">
          <form
            className="walkin-step"
            onSubmit={(e) => {
              e.preventDefault();
              if (valid && !busy) void submit();
            }}
          >
            <label className="walkin-field">
              <span>Naam</span>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="bijv. Drukker oktober 2026"
                maxLength={120}
                autoFocus
              />
            </label>
            <label className="walkin-field">
              <span>Aantal kaarten (max. 5000)</span>
              <input type="number" min={1} max={5000} value={count} onChange={(e) => setCount(e.target.value)} />
            </label>
            <label className="walkin-field">
              <span>Notitie (optioneel)</span>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <p className="muted">
              De kaarten zijn niet geactiveerd en hebben geen waarde tot ze aan de balie worden
              opgewaardeerd.
            </p>
            {error ? <div className="error-card">{error}</div> : null}
            <button type="submit" className="walkin-primary-btn" disabled={!valid || busy}>
              {busy ? 'Aanmaken…' : `${Number.isInteger(n) && n > 0 ? n : ''} kaarten aanmaken`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
